import React, { useCallback, useRef, useState } from 'react';
import { Mic, Square, Loader2 } from 'lucide-react';
import { api } from '../hooks/useApi';

interface InlineMicButtonProps {
  onTranscript: (text: string) => void;
  disabled?: boolean;
}

/**
 * Small mic button for inline voice-to-text on a single field.
 * Records audio, sends to POST /clinical/transcribe-text, returns plain text.
 */
export function InlineMicButton({ onTranscript, disabled }: InlineMicButtonProps) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/mp4';
      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: mimeType });
        if (blob.size === 0) return;

        setProcessing(true);
        try {
          const formData = new FormData();
          const ext = mimeType.includes('mp4') ? 'mp4' : 'webm';
          formData.append('audio', blob, `field-recording.${ext}`);

          const res = await api.post('/clinical/transcribe-text', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          if (res.data?.success && res.data?.data?.text) {
            onTranscript(res.data.data.text);
          }
        } catch {
          // Silently fail — user can type instead
        } finally {
          setProcessing(false);
        }
      };

      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecording(true);
    } catch {
      // Mic permission denied or unavailable
    }
  }, [onTranscript]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  }, []);

  if (processing) {
    return (
      <button type="button" disabled className="p-1 text-gray-400" title="Transcribing...">
        <Loader2 className="h-4 w-4 animate-spin" />
      </button>
    );
  }

  if (recording) {
    return (
      <button
        type="button"
        onClick={stopRecording}
        className="p-1 text-red-500 hover:text-red-700 animate-pulse"
        title="Stop recording"
      >
        <Square className="h-4 w-4" />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={startRecording}
      disabled={disabled}
      className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-30"
      title="Record voice note"
    >
      <Mic className="h-4 w-4" />
    </button>
  );
}
